import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import { useRegisterMutation } from "../../store/api/authApi";
import BasicInfoStep from "./components/BasicInfoStep";

const Register = () => {
	const navigate = useNavigate();
	const [register, { isLoading }] = useRegisterMutation();
	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		email: "",
		phone: "",
		password: "",
		agreeToTerms: false,
	});

	const [errors, setErrors] = useState<Record<string, string>>({});

	// Load saved draft on component mount
	useEffect(() => {
		const savedDraft = localStorage.getItem("registrationDraft");
		if (savedDraft) {
			try {
				const parsedDraft = JSON.parse(savedDraft);
				setFormData(parsedDraft);
			} catch (error) {
				console.error("Error loading saved draft:", error);
			}
		}
	}, []);

	const validateForm = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!formData.firstName.trim())
			newErrors.firstName = "First name is required";
		if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
		if (!formData.email.trim()) {
			newErrors.email = "Email is required";
		} else if (!/\S+@\S+\.\S+/.test(formData.email)) {
			newErrors.email = "Please enter a valid email address";
		}
		// Phone is optional; validate only if provided
		if (formData.phone.trim() && !/^\+?[\d\s\-\(\)]+$/.test(formData.phone)) {
			newErrors.phone = "Please enter a valid phone number";
		}
		if (!formData.password) {
			newErrors.password = "Password is required";
		} else if (formData.password.length < 8) {
			newErrors.password = "Password must be at least 8 characters long";
		}
		if (!formData.agreeToTerms) {
			newErrors.agreeToTerms =
				"You must agree to the Terms of Service and Privacy Policy to continue";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async () => {
		if (!validateForm()) return;

		setErrors({});

		try {
			// Prepare registration data (only basic fields, defaults to tenant)
			// Backend serializer expects camelCase field names
			// Normalize email to lowercase
			const normalizedEmail = formData.email.trim().toLowerCase();
			const registrationData = {
				email: normalizedEmail,
				password: formData.password,
				confirmPassword: formData.password,
				firstName: formData.firstName,
				lastName: formData.lastName,
				phone: formData.phone || "", // Backend expects string, not undefined
				agreeToTerms: formData.agreeToTerms, // Terms agreement required (frontend only)
			};

			// Call the registration API
			await register(registrationData).unwrap();

			// Clear saved draft
			localStorage.removeItem("registrationDraft");

			// Show success message and redirect to login
			navigate("/login", {
				state: {
					message:
						"Registration successful! Please log in with your credentials.",
				},
			});
		} catch (error: any) {
			console.error("Registration error:", error);

			// Handle different error types
			if (error?.data?.error) {
				setErrors({ submit: error.data.error });
			} else if (error?.data?.email) {
				setErrors({
					email: Array.isArray(error.data.email)
						? error.data.email[0]
						: error.data.email,
				});
			} else if (error?.data?.message) {
				setErrors({ submit: error.data.message });
			} else if (error?.status === "FETCH_ERROR") {
				setErrors({
					submit: "Network error. Please check your connection and try again.",
				});
			} else {
				setErrors({ submit: "Registration failed. Please try again." });
			}
		}
	};

	const canProceed = () => {
		return (
			formData.firstName &&
			formData.lastName &&
			formData.email &&
			formData.password &&
			formData.agreeToTerms
		);
	};

	return (
		<div className="min-h-screen bg-background">
			{/* Header */}
			<header className="bg-card border-b border-border">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex items-center justify-between h-16">
						<Link to="/" className="flex items-center space-x-3">
							<div className="w-8 h-8 bg-primary text-primary-foreground rounded-lg flex items-center justify-center">
								<Icon name="Home" size={20} />
							</div>
							<div>
								<h1 className="font-semibold text-lg text-foreground">
									Dofarahub
								</h1>
							</div>
						</Link>

						<div className="flex items-center space-x-4">
							<span className="text-sm text-muted-foreground">
								Already have an account?
							</span>
							<Link
								to="/login"
								className="text-sm font-medium text-primary hover:text-primary/80 smooth-transition"
							>
								Sign In
							</Link>
						</div>
					</div>
				</div>
			</header>

			{/* Main Content */}
			<main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				<div className="bg-card rounded-lg border border-border card-shadow">
					<div className="p-6 sm:p-8 lg:p-12">
						{/* Error Message */}
						{errors.submit && (
							<div className="mb-6 bg-error/10 border border-error/20 text-error px-4 py-3 rounded-lg">
								<div className="flex items-center space-x-2">
									<Icon name="AlertCircle" size={16} />
									<span className="text-sm font-medium">{errors.submit}</span>
								</div>
							</div>
						)}

						{/* Form Content */}
						<div className="mb-8">
							<BasicInfoStep
								formData={formData}
								setFormData={setFormData}
								errors={errors}
								setErrors={setErrors}
							/>
						</div>

						{/* Submit Button */}
						<div className="flex justify-end pt-6 border-t border-border">
							<Button
								type="button"
								onClick={handleSubmit}
								disabled={!canProceed() || isLoading}
								className="w-full sm:w-auto"
							>
								{isLoading ? (
									<>
										<Icon
											name="Loader2"
											size={16}
											className="mr-2 animate-spin"
										/>
										Creating Account...
									</>
								) : (
									<>Create Account</>
								)}
							</Button>
						</div>
					</div>
				</div>

				{/* Help Section */}
				<div className="mt-8 text-center">
					<p className="text-sm text-muted-foreground mb-4">
						Need help with registration? Contact our support team.
					</p>
					<div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-6">
						<a
							href="mailto:support@dofara.com"
							className="flex items-center space-x-2 text-sm text-primary hover:text-primary/80 smooth-transition"
						>
							<Icon name="Mail" size={16} />
							<span>support@dofara.com</span>
						</a>
						{/* <a
							href="tel:+1-555-0123"
							className="flex items-center space-x-2 text-sm text-primary hover:text-primary/80 smooth-transition"
						>
							<Icon name="Phone" size={16} />
							<span>+1 (555) 012-3456</span>
						</a> */}
					</div>
				</div>
			</main>

			{/* Footer */}
			<footer className="bg-card border-t border-border mt-16">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
					<div className="text-center">
						<p className="text-sm text-muted-foreground">
							© {new Date().getFullYear()} Dofarahub. All rights reserved.
						</p>
						<div className="flex items-center justify-center space-x-6 mt-4">
							<Link
								to="/terms"
								className="text-xs text-muted-foreground hover:text-foreground smooth-transition"
							>
								Terms of Service
							</Link>
							<Link
								to="/privacy"
								className="text-xs text-muted-foreground hover:text-foreground smooth-transition"
							>
								Privacy Policy
							</Link>
							<Link
								to="/help"
								className="text-xs text-muted-foreground hover:text-foreground smooth-transition"
							>
								Help Center
							</Link>
						</div>
					</div>
				</div>
			</footer>
		</div>
	);
};

export default Register;
